/**
 * Newsletter Service
 *
 * Manages newsletter subscriptions using Cloudflare D1 API.
 * Falls back to localStorage for development/offline mode.
 */

const API_BASE = '/api';
const STORAGE_KEY = 'newsletter_subscribers';

// Check if we're in production (API available)
const isProduction = () => {
  return window.location.hostname !== 'localhost' &&
         !window.location.hostname.includes('127.0.0.1');
};

/**
 * Get all newsletter subscribers
 * @returns {Promise<Array>} Array of subscriber objects
 */
export const getSubscribers = async () => {
  if (isProduction()) {
    try {
      const response = await fetch(`${API_BASE}/subscribers`);
      const data = await response.json();
      return data.success ? data.subscribers : [];
    } catch (error) {
      console.error('API error, falling back to localStorage:', error);
    }
  }

  // Fallback to localStorage
  try {
    const data = localStorage.getItem(STORAGE_KEY);
    return data ? JSON.parse(data) : [];
  } catch (error) {
    console.error('Error reading subscribers:', error);
    return [];
  }
};

/**
 * Add a new subscriber
 * @param {string} email - Subscriber's email address
 * @returns {Promise<Object>} Result object with success status and message
 */
export const addSubscriber = async (email) => {
  const source = window.location.pathname || '/';

  if (isProduction()) {
    try {
      const response = await fetch(`${API_BASE}/subscribers`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, source })
      });
      const data = await response.json();
      return data;
    } catch (error) {
      console.error('API error:', error);
      return {
        success: false,
        message: 'Error de conexión. Por favor, inténtalo de nuevo.'
      };
    }
  }

  // Fallback to localStorage for development only
  try {
    const subscribers = getSubscribersSync();

    const exists = subscribers.some(
      (sub) => sub.email.toLowerCase() === email.toLowerCase()
    );

    if (exists) {
      return {
        success: false,
        message: 'Este email ya está suscrito a nuestro boletín.'
      };
    }

    const newSubscriber = {
      id: Date.now().toString(),
      email: email.toLowerCase().trim(),
      subscribedAt: new Date().toISOString(),
      source,
      status: 'active'
    };

    subscribers.push(newSubscriber);
    localStorage.setItem(STORAGE_KEY, JSON.stringify(subscribers));

    return {
      success: true,
      message: '¡Gracias por suscribirte a nuestro boletín!',
      subscriber: newSubscriber
    };
  } catch (error) {
    console.error('Error adding subscriber:', error);
    return {
      success: false,
      message: 'Error al procesar la suscripción. Inténtalo de nuevo.'
    };
  }
};

/**
 * Remove a subscriber by ID
 * @param {string} id - Subscriber ID
 * @returns {Promise<boolean>} Success status
 */
export const removeSubscriber = async (id) => {
  if (isProduction()) {
    try {
      const response = await fetch(`${API_BASE}/subscribers?id=${id}`, {
        method: 'DELETE'
      });
      const data = await response.json();
      return data.success;
    } catch (error) {
      console.error('API error, falling back to localStorage:', error);
    }
  }

  // Fallback to localStorage
  try {
    const subscribers = getSubscribersSync();
    const filtered = subscribers.filter((sub) => sub.id !== id);
    localStorage.setItem(STORAGE_KEY, JSON.stringify(filtered));
    return true;
  } catch (error) {
    console.error('Error removing subscriber:', error);
    return false;
  }
};

/**
 * Update subscriber status
 * @param {string} id - Subscriber ID
 * @param {string} status - New status ('active' or 'unsubscribed')
 * @returns {Promise<boolean>} Success status
 */
export const updateSubscriberStatus = async (id, status) => {
  if (isProduction()) {
    try {
      const response = await fetch(`${API_BASE}/subscribers`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id, status })
      });
      const data = await response.json();
      return data.success;
    } catch (error) {
      console.error('API error, falling back to localStorage:', error);
    }
  }

  // Fallback to localStorage
  try {
    const subscribers = getSubscribersSync();
    const index = subscribers.findIndex((sub) => sub.id === id);

    if (index !== -1) {
      subscribers[index].status = status;
      subscribers[index].updatedAt = new Date().toISOString();
      localStorage.setItem(STORAGE_KEY, JSON.stringify(subscribers));
      return true;
    }
    return false;
  } catch (error) {
    console.error('Error updating subscriber:', error);
    return false;
  }
};

/**
 * Sanitize cell value to prevent CSV injection attacks
 * @param {string} cell - Cell value to sanitize
 * @returns {string} Sanitized cell value
 */
const sanitizeCSVCell = (cell) => {
  const str = String(cell || '');
  // Prevent formula injection by prefixing dangerous characters
  if (/^[=+\-@\t\r]/.test(str)) {
    return `'${str}`;
  }
  // Escape double quotes
  return str.replace(/"/g, '""');
};

/**
 * Export subscribers as CSV
 * @param {Array} subscribers - Array of subscribers
 * @returns {string} CSV formatted string
 */
export const exportToCSV = (subscribers) => {
  if (!subscribers || subscribers.length === 0) {
    return '';
  }

  const headers = ['Email', 'Fecha de Suscripción', 'Origen', 'Estado'];
  const rows = subscribers.map((sub) => [
    sanitizeCSVCell(sub.email),
    new Date(sub.subscribed_at || sub.subscribedAt).toLocaleString('es-ES'),
    sanitizeCSVCell(sub.source),
    sub.status === 'active' ? 'Activo' : 'Cancelado'
  ]);

  const csvContent = [
    headers.join(','),
    ...rows.map((row) => row.map((cell) => `"${cell}"`).join(','))
  ].join('\n');

  return csvContent;
};

/**
 * Download CSV from API endpoint
 * Downloads CSV file directly from the server endpoint
 * @returns {Promise<boolean>} Success status
 */
export const downloadCSVFromAPI = async () => {
  if (isProduction()) {
    try {
      const response = await fetch(`${API_BASE}/subscribers/export`);

      if (!response.ok) {
        console.error('Failed to download CSV');
        return false;
      }

      // Get the blob and create download link
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;

      // Get filename from Content-Disposition header or use default
      const contentDisposition = response.headers.get('Content-Disposition');
      const filename = contentDisposition
        ? contentDisposition.split('filename=')[1]?.replace(/"/g, '')
        : `suscriptores_${new Date().toISOString().split('T')[0]}.csv`;

      link.setAttribute('download', filename);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      return true;
    } catch (error) {
      console.error('Error downloading CSV:', error);
      return false;
    }
  }

  return false;
};

/**
 * Get subscriber statistics
 * @returns {Promise<Object>} Statistics object
 */
export const getStatistics = async () => {
  if (isProduction()) {
    try {
      const response = await fetch(`${API_BASE}/subscribers`);
      const data = await response.json();
      return data.success ? data.statistics : getDefaultStats();
    } catch (error) {
      console.error('API error, falling back to localStorage:', error);
    }
  }

  // Fallback to localStorage
  const subscribers = getSubscribersSync();

  const total = subscribers.length;
  const active = subscribers.filter((sub) => sub.status === 'active').length;
  const unsubscribed = subscribers.filter((sub) => sub.status === 'unsubscribed').length;

  const oneWeekAgo = new Date();
  oneWeekAgo.setDate(oneWeekAgo.getDate() - 7);
  const recentSubscribers = subscribers.filter(
    (sub) => new Date(sub.subscribedAt) >= oneWeekAgo
  ).length;

  return { total, active, unsubscribed, recentSubscribers };
};

// Sync version for localStorage fallback
function getSubscribersSync() {
  try {
    const data = localStorage.getItem(STORAGE_KEY);
    return data ? JSON.parse(data) : [];
  } catch {
    return [];
  }
}

function getDefaultStats() {
  return { total: 0, active: 0, unsubscribed: 0, recentSubscribers: 0 };
}
